/**
 * Smart API: Manual Checkout feature.
 * (2026-04-24, Jason Cox) Extracted from the 2800-line unifiedApiService.ts
 * monolith. The SmartApiAdapter now delegates to these free functions so
 * each feature owns its own types + implementation in one small file.
 *
 * Endpoints:
 *   POST /scm/v1/rfid/lookupTags
 *   POST /scm/v1/rfid/manualCheckout
 *   POST /scm/v1/rfid/manualCheckoutHistory
 */
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import type { ApiConfig } from '../apiConfig';

// (2026-04-16, Jason Cox) Manual Checkout types.
export interface ManualCheckoutTagInfo {
  rfid: string;
  status: number | null;
  statusName: string;
  sku?: string;
  itemName?: string;
  // (2026-04-20, Jason Cox) smartboxId is used by the UI to scope the customer
  // dropdown and to detect mixed-smartbox batches (which block checkout).
  smartboxId?: number;
  smartboxName?: string;
  workcenterName?: string;
  clientName?: string;
  canCheckout: boolean;
  canCheckin: boolean;
  packageQty?: number;
}

// (2026-04-17 / 2026-04-24, Jason Cox) Picker name & company are NOT sent --
// the backend derives them from the authenticated session. Associated Party
// is a free-text label going forward (was a dropdown + customerId in 2026-04-21
// design; the id is no longer sent).
export interface ManualCheckoutPayload {
  rfids: string[];
  associatedParty: string;  // free-text party label; recorded in ticket note blob
  foreman?: string;         // customer foreman or approver
  location?: string;        // free-text site / lease / well / property name
  // (2026-06-11, Jason Cox) Picker identity is now free-text from the form
  // because deployments share one login. When sent, the backend uses these
  // instead of the session-derived name/company; when omitted it falls back to
  // the session (backward compat with older builds).
  pickerName?: string;      // who physically picked up the tags
  pickerCompany?: string;   // the picker's company
  // (2026-10-23, Jason Cox) Picker's callback number. Required by the TXAM form;
  // optional on the API so the Work Order variant (and older builds) omit it.
  // Stored as a "Picker Phone:" line on the ticket note (backend caps it at 20).
  pickerPhone?: string;
  // (2026-06-11, Jason Cox) `signature` superseded the typed `initials` field:
  // a base64 PNG data URL of a drawn signature. The backend uploads it to S3
  // (keyed by docNum) and stores only a marker in the note blob. `initials` is
  // retained as optional for backward compatibility but is no longer sent by
  // the PWA UI.
  signature?: string;       // base64 PNG data URL (e.g. "data:image/png;base64,...")
  initials?: string;        // DEPRECATED: picker's initials / typed signature
}

export interface ManualCheckoutResult {
  processed: { rfid: string; docNum: string; headId: number }[];
  errors: { rfid: string; error: string }[];
}

export interface ManualCheckoutHistoryOptions {
  page?: number;
  pageSize?: number;
  rfid?: string;
  sku?: string;
  customerId?: number;
  startTime?: string;
  endTime?: string;
}

// (2026-04-17 / 2026-04-21, Jason Cox) History row shape. associatedParty /
// foreman / pickerCompany / initials are parsed server-side from the
// md_rfid.note labeled-lines blob.
export interface ManualCheckoutHistoryRow {
  docNum: string;
  rfid: string;
  sku?: string;
  itemName?: string;
  clientName?: string;
  /** Dropdown selection at checkout time (customer or vendor), display name. */
  associatedParty?: string;
  foreman?: string;
  location?: string;
  pickerName?: string;
  pickerCompany?: string;
  /** (2026-10-23) Parsed from the "Picker Phone:" note line; empty on older tickets. */
  pickerPhone?: string;
  initials?: string;
  // (2026-06-11, Jason Cox) Public S3 URL of the drawn signature image, rebuilt
  // server-side from the ticket docNum when the note carries the signature
  // marker. Empty/absent for legacy tickets that stored typed `initials`.
  signatureUrl?: string;
  docDate: string;
  docTime: string;
  checkoutBy?: string;
  currentStatusName: string;
  isCheckedOut: boolean;
  ticketType: 'checkout' | 'checkin';
}

export interface ManualCheckoutHistoryResult {
  data: ManualCheckoutHistoryRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

export async function lookupCheckoutTags(
  config: ApiConfig,
  rfids: string[],
): Promise<ManualCheckoutTagInfo[]> {
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/rfid/lookupTags`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify({ rfids }),
    },
    { maxRetries: 1, timeout: 10000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'lookupTags failed');
  return (data.data || []) as ManualCheckoutTagInfo[];
}

/**
 * (2026-09-18, Jason Cox) Raised from 15s. A manual checkout is NOT idempotent:
 * it writes a ticket head + line + inventory rows per tag inside one
 * transaction, then uploads the signature. A client timeout shorter than the
 * server's own limit is actively harmful -- the request keeps running and
 * commits while the PWA reports failure, which is how the same tag got two
 * delivery tickets 25 seconds apart (TXAM batch, 2026-05-18). At 60s a timeout
 * almost always means the network dropped rather than the server still working.
 */
const CHECKOUT_TIMEOUT_MS = 60000;

/**
 * Thrown when the checkout POST never produced a usable response -- timeout,
 * abort, dropped connection, truncated body.
 *
 * The distinction matters: the request may or may not have committed, so the
 * caller MUST verify before letting anyone resubmit. An API-level failure
 * (`code !== 0`) stays a plain Error, because the backend answered and its
 * transaction rolled back -- that one is safe to retry.
 */
export class IndeterminateCheckoutError extends Error {
  readonly indeterminate = true;

  constructor(message: string) {
    super(message);
    this.name = 'IndeterminateCheckoutError';
    // Reason: restores the prototype chain so `instanceof` survives the
    // ES5 downlevel target CRA compiles to.
    Object.setPrototypeOf(this, IndeterminateCheckoutError.prototype);
  }
}

export async function submitManualCheckout(
  config: ApiConfig,
  payload: ManualCheckoutPayload,
): Promise<ManualCheckoutResult> {
  let data: any;
  try {
    const response = await fetchWithRetry(
      `${config.baseUrl}/scm/v1/rfid/manualCheckout`,
      {
        method: 'POST',
        headers: config.authHeaders,
        body: JSON.stringify(payload),
      },
      { maxRetries: 1, timeout: CHECKOUT_TIMEOUT_MS },
    );
    data = await response.json();
  } catch (err: any) {
    throw new IndeterminateCheckoutError(
      err?.message || 'the checkout request did not complete',
    );
  }
  if (data.code !== 0) throw new Error(data.msg || 'manualCheckout failed');
  return data.data as ManualCheckoutResult;
}

/**
 * Fetch a stored signature image for one ticket.
 *
 * The signature blob lives in a PRIVATE cloud container, so its URL on a history
 * row is not directly loadable in an <img>. This endpoint reads the blob with the
 * account credential (server-side) and returns it as a base64 data URL, which the
 * UI can render or the billing report can embed.
 *
 * Returns null when the ticket has no stored signature (backend code != 0, e.g.
 * "no signature stored"), so a caller can cleanly fall back to a local copy.
 */
export async function getManualCheckoutSignature(
  config: ApiConfig,
  docNum: string,
): Promise<string | null> {
  if (!docNum) return null;
  let response;
  try {
    response = await fetchWithRetry(
      `${config.baseUrl}/scm/v1/rfid/manualCheckoutSignature`,
      {
        method: 'POST',
        headers: config.authHeaders,
        body: JSON.stringify({ docNum }),
      },
      { maxRetries: 1, timeout: 15000 },
    );
  } catch {
    return null; // network/endpoint error -> caller falls back to local
  }
  const data = await response.json().catch(() => null);
  if (!data || data.code !== 0) return null;
  const sig = data.data && data.data.signature;
  return typeof sig === 'string' && sig ? sig : null;
}

export async function getManualCheckoutHistory(
  config: ApiConfig,
  options: ManualCheckoutHistoryOptions = {},
): Promise<ManualCheckoutHistoryResult> {
  const body = {
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 25,
    rfid: options.rfid ?? '',
    sku: options.sku ?? '',
    customerId: options.customerId,
    startTime: options.startTime ?? '',
    endTime: options.endTime ?? '',
  };
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/rfid/manualCheckoutHistory`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 15000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'manualCheckoutHistory failed');
  const payload = data.data || {};
  return {
    data: payload.data || [],
    total: payload.total || 0,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}
